import { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Mail } from "lucide-react";
import "../sash/sash.css";
import { sisters, collectionSlides } from "../sash/data";
import { Reveal, Marquee, MaskedHeadline, PillButton } from "../sash/ui";
import { SignupForm } from "../sash/SignupForm";
import { ProductQuiz } from "../sash/ProductQuiz";
import { SisterQuiz } from "../sash/SisterQuiz";
import { ProductIdea } from "../sash/ProductIdea";
import { Faq } from "../sash/Faq";
import { Returns } from "../sash/Returns";

const marqueeItems = ["Next era loading", "The show isn't over", "We're just changing the set", "Come play while we get ready"];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const IntermissionBar = () => (
  <div className="intermission-bar w-full py-2.5 px-4 flex items-center justify-center gap-3 text-center" data-testid="intermission-bar">
    <span className="opacity-70">✦</span>
    <span className="text-[0.74rem] font-bold uppercase" style={{ letterSpacing: "0.16em" }}>Sash Intermission · The next era is loading</span>
    <span className="opacity-70">✦</span>
  </div>
);

const Hero = () => (
  <section className="relative px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12 text-center" data-testid="hero-section">
    <div className="max-w-[880px] mx-auto">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}>
        <span className="eyebrow">Next Era Loading</span>
      </motion.div>
      <div className="mt-6 flex justify-center">
        <MaskedHeadline
          className="sash-serif text-[var(--ink)] leading-[1.04] text-[clamp(2.4rem,6.5vw,5rem)] font-medium"
          lines={[
            "Sash isn't gone.",
            "We're getting ready",
            <>for our next <span className="italic" style={{ color: "var(--clay)" }}>chapter.</span></>,
          ]}
        />
      </div>
      <p className="mt-7 text-[1.05rem] md:text-lg leading-relaxed text-[var(--ink-soft)] max-w-[42rem] mx-auto">
        A new team. A fresh start. And plenty of Sash magic still ahead. We're building the next version of Sash behind the scenes. In the meantime, find your perfect Sash, dream up a bag of your own, tell us what you want to see next, and make sure you're on the list.
      </p>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <PillButton onClick={() => scrollTo("relaunch-final")} testid="hero-cta-primary">Tell Me When Sash Is Back</PillButton>
        <PillButton variant="ghost" onClick={() => scrollTo("product-quiz")} testid="hero-cta-secondary">Find My Perfect Sash</PillButton>
      </div>

      <div className="mt-10 flex justify-center">
        <SignupForm source="hero" testid="hero-signup" />
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-[var(--line)]" data-testid="about-section">
    <div className="max-w-[1160px] mx-auto grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal className="side-img grain">
        <img src="/sash/render-4.png" alt="A Sash Sister, hands free and out living her life" loading="lazy" />
      </Reveal>
      <div>
        <Reveal>
          <span className="eyebrow">Wait. What's Sash?</span>
          <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] leading-[1.04]">The hands-free bag that accidentally became a whole community.</h2>
        </Reveal>
        <div className="mt-6 space-y-4 max-w-[34rem] text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
          <Reveal delay={0.05}><p>It began with a simple idea: a bag that keeps everything you need close to your body, spreads out the weight more comfortably, and lets you move through your day without constantly managing a traditional handbag.</p></Reveal>
          <Reveal delay={0.1}><p>Then something much bigger happened. People fell in love with the bag — and they found each other. Over the years Sash became part product, part community, and part wonderfully weird little universe full of women who proudly call themselves Sash Sisters.</p></Reveal>
          <Reveal delay={0.15}><p>Now Sash is preparing for its next era. The company isn't going away — operations are moving to a new team, and we're working toward reopening the website on October 11.</p></Reveal>
        </div>
      </div>
    </div>
  </section>
);

const ServiceCallout = () => (
  <section className="px-6 md:px-12 lg:px-16 pt-14 md:pt-16" data-testid="service-callout-section">
    <div className="max-w-[960px] mx-auto">
      <Reveal>
        <div
          className="rounded-3xl border p-7 md:p-10 grain relative overflow-hidden"
          style={{ background: "rgba(26,138,126,0.08)", borderColor: "rgba(26,138,126,0.35)" }}
        >
          <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl grid place-items-center" style={{ background: "var(--tan)", color: "var(--cream)" }}>
              <Mail size={26} strokeWidth={2} />
            </div>
            <div className="flex-1">
              <span className="eyebrow">We're still here — behind the scenes</span>
              <h2 className="mt-2 text-[clamp(1.5rem,3vw,2.2rem)] leading-tight sash-serif">
                Need to reach Customer Service?
              </h2>
              <p className="mt-3 text-[1.02rem] leading-relaxed text-[var(--ink-soft)] max-w-[46rem]">
                The shop is offline, but our team absolutely isn't. For existing or outstanding orders,
                shipping questions, pre-orders, refunds, or anything else — email us and we'll take care of you.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a href="mailto:info@thesashbag.com" className="cta" data-testid="service-email-cta">
                <Mail size={18} strokeWidth={2.2} /> Email Customer Service
              </a>
              <p className="mt-3 text-center md:text-left text-sm font-semibold" style={{ color: "var(--tan-deep)" }}>
                info@thesashbag.com
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CommunityBand = () => (
  <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-[var(--line)]" data-testid="community-band">
    <div className="max-w-[1160px] mx-auto text-center">
      <Reveal>
        <span className="eyebrow">Sash Meetup 2024</span>
        <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.2rem)] leading-[1.03]">This is the <span className="italic" style={{ color: "var(--clay)" }}>sisterhood.</span></h2>
        <p className="mt-4 text-[1.05rem] text-[var(--ink-soft)] max-w-[38rem] mx-auto">
          Real women, real bags, real friendships. This is what happens when a handbag quietly turns into a community — and it isn't going anywhere.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="side-img grain mt-10 max-w-[1000px] mx-auto">
          <img src="/sash/sash-backyard.jpg" alt="Sash Sisters gathered together at the 2024 meetup" loading="lazy" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Slideshow = ({ slides, prefix }) => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const n = slides.length;
  const go = (d) => { setDir(d); setIdx((i) => (i + d + n) % n); };
  const goto = (i) => { setDir(i > idx ? 1 : -1); setIdx(i); };
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => { setDir(1); setIdx((i) => (i + 1) % n); }, 4500);
    return () => clearTimeout(t);
  }, [idx, paused, n]);
  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 70 : -70 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -70 : 70 }),
  };
  return (
    <>
      <Reveal delay={0.12}>
        <div className="ss-stage grain mt-10" style={{ height: "min(72vh, 640px)" }}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid={`${prefix}-stage`}>
          <AnimatePresence mode="popLayout" custom={dir} initial={false}>
            <motion.img key={idx} src={slides[idx].img} alt={slides[idx].caption} custom={dir} variants={variants}
              initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
              className="absolute inset-0 w-full h-full object-contain" style={{ zIndex: 1 }} />
          </AnimatePresence>
          <button className="ss-arrow" style={{ left: 16 }} onClick={() => go(-1)} aria-label="Previous" data-testid={`${prefix}-prev`}><ChevronLeft size={22} strokeWidth={2.4} /></button>
          <button className="ss-arrow" style={{ right: 16 }} onClick={() => go(1)} aria-label="Next" data-testid={`${prefix}-next`}><ChevronRight size={22} strokeWidth={2.4} /></button>
          <div className="ss-caption">{slides[idx].caption}</div>
        </div>
      </Reveal>
      <div className="mt-6 flex items-center justify-center gap-2.5 flex-wrap">
        {slides.map((_, i) => (
          <button key={i} className={`ss-dot ${i === idx ? "active" : ""}`} onClick={() => goto(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`${prefix}-dot-${i}`} />
        ))}
      </div>
    </>
  );
};

const SashSisters = () => (
  <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-[var(--line)]" data-testid="sash-sisters-section">
    <div className="max-w-[1000px] mx-auto text-center">
      <Reveal><span className="eyebrow">The Sash Sisterhood</span></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.03]">Meet the <span className="italic" style={{ color: "var(--clay)" }}>Sash Sisters.</span></h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-4 text-[1.05rem] text-[var(--ink-soft)] max-w-[34rem] mx-auto">Thousands of women, hands free and living their lives — from farmers markets to the foot of the Eiffel Tower.</p>
      </Reveal>
      <Slideshow slides={sisters} prefix="ss" />
    </div>
  </section>
);

const CollectionShowcase = () => (
  <section className="section-dark grain relative px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="sash-collections-section">
    <div className="max-w-[1000px] mx-auto text-center relative z-10">
      <Reveal><span className="eyebrow">The Sisterhood Collections</span></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.03] text-[var(--cream)]">A whole <span className="italic" style={{ color: "var(--tan)" }}>rainbow</span> of Sash.</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-4 text-[1.05rem] text-[var(--sand)] max-w-[34rem] mx-auto">Once you find your first, you start collecting them by color, leather and mood — a growing sisterhood of bags.</p>
      </Reveal>
      <Slideshow slides={collectionSlides} prefix="col" />
    </div>
  </section>
);

const FinalRelaunch = () => (
  <section id="relaunch-final" className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-[var(--line)]" data-testid="final-relaunch-section">
    <div className="max-w-[820px] mx-auto text-center">
      <Reveal><span className="eyebrow">Be here when the doors open</span></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-5 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.0]">You'll want to be here when the <span className="italic" style={{ color: "var(--clay)" }}>doors open.</span></h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 text-lg text-[var(--ink-soft)] max-w-[38rem] mx-auto">Sash is entering a new chapter and we can't wait to show you what's next. Join the relaunch list and we'll let you know when the website is live, what's coming back, what's new, and how to get first dibs.</p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-9 flex justify-center"><SignupForm source="general_relaunch_signup" label="Tell Me When Sash Is Back" testid="final-signup" /></div>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer className="section-dark px-6 md:px-12 lg:px-16 py-12" data-testid="footer">
    <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <span className="sash-serif italic text-2xl text-[var(--cream)]">Sash</span>
      <p className="text-sm text-[var(--sand)]">© {new Date().getFullYear()} Sash. The show isn't over — we're just changing the set.</p>
    </div>
  </footer>
);

export default function SashOffer() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="sash" data-testid="sash-offer-page">
      <IntermissionBar />
      <Hero />
      <ServiceCallout />
      <Returns />
      <Marquee items={marqueeItems} />
      <About />
      <CommunityBand />
      <ProductQuiz />
      <SisterQuiz />
      <ProductIdea />
      <SashSisters />
      <CollectionShowcase />
      <Faq />
      <FinalRelaunch />
      <Footer />
    </div>
  );
}
